import { api, queryKeys } from '@alpha/api-client';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Modal } from '../ui';

const SEEN_KEY_PREFIX = 'alpha_news_popup_seen_';

function hasBeenSeen(id: string): boolean {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(SEEN_KEY_PREFIX + id) === '1';
}

function markSeen(id: string): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SEEN_KEY_PREFIX + id, '1');
}

/**
 * Surfaces the latest admin-posted news item that carries an image as a
 * one-time popup — an admin-posted offer/banner should be prominent on first
 * view, not buried in the Support > News list.
 *
 * "Once per news item per browser": keyed by the news item's id in
 * localStorage, so re-visiting the dashboard (or a different news item later)
 * does not repeat the same popup, but a NEW image-bearing item shows again.
 */
export function NewsPopup() {
  const { data } = useQuery({
    queryKey: queryKeys.news(),
    queryFn: () => api.support.news().then((r) => r.data),
  });

  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    // News is returned latest-first; show the newest unseen item with an image.
    const candidate = data.find((item) => item.image_url && !hasBeenSeen(item.id));
    if (candidate) setActiveId(candidate.id);
  }, [data]);

  const item = data?.find((n) => n.id === activeId);

  if (!item || !item.image_url) return null;

  const close = () => {
    markSeen(item.id);
    setActiveId(null);
  };

  return (
    <Modal open onClose={close} title={item.title} size="md">
      <img
        src={item.image_url}
        alt={item.title}
        style={{ width: '100%', borderRadius: 8, display: 'block' }}
      />
      {item.body ? <p style={{ marginTop: 12 }}>{item.body}</p> : null}
    </Modal>
  );
}
